
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export type Voucher = {
  id: string;
  code: string;
  campaign: string;
  status: "active" | "claimed" | "redeemed" | "expired";
  expires: Date;
  claimed?: {
    by: string;
    date: Date;
  };
  redeemed?: {
    date: Date;
    location: string;
  };
};

type Props = {
  vouchers: Voucher[];
  onViewVoucher: (voucher: Voucher) => void;
  isLoading?: boolean;
};

export function VoucherList({ vouchers, onViewVoucher, isLoading = false }: Props) {
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredVouchers = vouchers.filter(
    (voucher) =>
      voucher.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      voucher.campaign.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusColor = {
    active: "bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400",
    claimed: "bg-purple-100 text-purple-800 dark:bg-purple-800/20 dark:text-purple-400",
    redeemed: "bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400",
    expired: "bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400",
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <div className="relative w-full md:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search vouchers..."
            className="pl-8 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Campaign</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`loading-${index}`}>
                  <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-8 rounded-md" /></TableCell>
                </TableRow>
              ))
            ) : filteredVouchers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No vouchers found.
                </TableCell>
              </TableRow>
            ) : (
              filteredVouchers.map((voucher) => (
                <TableRow key={voucher.id}>
                  <TableCell className="font-mono">{voucher.code}</TableCell>
                  <TableCell>{voucher.campaign}</TableCell>
                  <TableCell>
                    <Badge className={statusColor[voucher.status]}>
                      {voucher.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {voucher.expires.toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewVoucher(voucher)}
                    >
                      <Eye size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
